import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, DetachedRouteHandle, RouteReuseStrategy } from '@angular/router';

import { NavigationTabsService } from './navigation-tabs.service';

interface CachedRoute {
  tabId: string;
  handle: DetachedRouteHandle;
}

interface AngularDetachedRouteHandle {
  componentRef?: {
    destroy(): void;
  };
}

@Injectable()
export class TabRouteReuseStrategy implements RouteReuseStrategy {
  /**
   * Rutas desacopladas que siguen vivas mientras su pestaña permanezca abierta.
   *
   * IMPORTANTE:
   * retrieve() NO elimina el handle.
   *
   * Angular 19 puede consultar retrieve() más de una vez durante una misma
   * navegación. Angular mismo llama después a store(route, null) cuando el
   * componente ya fue re-adjuntado; en ese momento solamente quitamos la
   * referencia del Map, sin destruir el componente.
   */
  private readonly handles = new Map<string, CachedRoute>();

  constructor(private readonly tabsService: NavigationTabsService) {}

  /**
   * Decide si la ruta actual debe quedar viva al navegar a otra pestaña.
   *
   * Angular solamente puede desacoplar rutas que tengan un componente
   * asociado a un RouterOutlet.
   */
  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    if (!route.component) {
      return false;
    }

    const path = this.getPath(route);
    const tabId = this.findTabId(path);

    return tabId !== null && this.isTabOpen(tabId);
  }

  /**
   * Guarda el componente desacoplado.
   *
   * Angular también llama store(route, null) después de re-adjuntar una ruta.
   * Ese null significa "elimina la referencia almacenada"; NO significa
   * "destruye el componente", porque en ese instante el componente vuelve a
   * estar activo en pantalla.
   */
  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
    const key = this.getKey(route);

    if (handle === null) {
      // MUY IMPORTANTE:
      // Angular acaba de re-adjuntar este componente.
      // Solo quitamos la referencia del caché.
      // NO llamamos destroyHandle().
      this.handles.delete(key);
      return;
    }

    const path = this.getPath(route);
    const tabId = this.findTabId(path);

    if (!tabId || !this.isTabOpen(tabId)) {
      return;
    }

    const previous = this.handles.get(key);

    // Si por alguna razón ya existía otro handle para la misma clave,
    // destruimos únicamente el handle viejo.
    if (previous && previous.handle !== handle) {
      this.destroyHandle(previous.handle);
    }

    this.handles.set(key, {
      tabId,
      handle,
    });
  }

  /**
   * Indica si existe una instancia guardada que puede volver a usarse.
   */
  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    const key = this.getKey(route);
    const cached = this.handles.get(key);

    if (!cached) {
      return false;
    }

    if (!this.isTabOpen(cached.tabId)) {
      // La pestaña fue cerrada mientras el componente estaba en caché.
      this.destroyHandle(cached.handle);
      this.handles.delete(key);
      return false;
    }

    return true;
  }

  /**
   * Devuelve el MISMO handle almacenado.
   *
   * NO debe eliminarse aquí.
   *
   * Angular 19.2.17 puede llamar retrieve() durante la creación del RouterState
   * y posteriormente volver a llamarlo durante la activación del RouterOutlet.
   * Si lo eliminamos en la primera llamada, la segunda llamada no encuentra el
   * componente y Angular termina creando una instancia nueva.
   */
  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    const key = this.getKey(route);
    const cached = this.handles.get(key);

    if (!cached) {
      return null;
    }

    if (!this.isTabOpen(cached.tabId)) {
      this.destroyHandle(cached.handle);
      this.handles.delete(key);
      return null;
    }

    // NO HACER:
    // this.handles.delete(key);

    return cached.handle;
  }

  /**
   * Comportamiento normal de Angular cuando la configuración de la ruta
   * futura y la actual son exactamente la misma.
   */
  shouldReuseRoute(future: ActivatedRouteSnapshot, current: ActivatedRouteSnapshot): boolean {
    return future.routeConfig === current.routeConfig;
  }

  /**
   * Se llama únicamente cuando el usuario CIERRA una pestaña.
   *
   * Aquí sí destruimos los componentes almacenados porque ya no deben volver.
   */
  clearTab(tabId: string): void {
    const keysToDelete: string[] = [];

    // Map.forEach() evita el TS2802 que aparecía con ...map.entries().
    this.handles.forEach((cached, key) => {
      if (cached.tabId === tabId) {
        keysToDelete.push(key);
      }
    });

    for (let i = 0; i < keysToDelete.length; i++) {
      const key = keysToDelete[i];
      const cached = this.handles.get(key);

      if (cached) {
        this.destroyHandle(cached.handle);
      }

      this.handles.delete(key);
    }
  }

  /**
   * Se llama al cerrar/reiniciar la sesión.
   */
  clearAll(): void {
    // Map.forEach() evita el TS2802 que aparecía con ...map.values().
    this.handles.forEach((cached) => {
      this.destroyHandle(cached.handle);
    });

    this.handles.clear();
  }

  /**
   * Construye el path real de la ruta.
   *
   * Ejemplo:
   * /contabilidad/comprobantes/editar/25
   */
  private getPath(route: ActivatedRouteSnapshot): string {
    const segments: string[] = [];
    const snapshots = route.pathFromRoot;

    for (let i = 0; i < snapshots.length; i++) {
      const snapshot = snapshots[i];

      for (let j = 0; j < snapshot.url.length; j++) {
        const segment = snapshot.url[j];

        if (segment.path) {
          segments.push(segment.path);
        }
      }
    }

    return segments.length > 0 ? `/${segments.join('/')}` : '/';
  }

  /**
   * Genera una clave estable para poder encontrar exactamente la misma ruta
   * cuando el usuario vuelve a la pestaña.
   */
  private getKey(route: ActivatedRouteSnapshot): string {
    const path = this.getPath(route);

    const routeConfigParts: string[] = [];
    const snapshots = route.pathFromRoot;

    for (let i = 0; i < snapshots.length; i++) {
      const snapshot = snapshots[i];

      routeConfigParts.push(`${snapshot.outlet}:${snapshot.routeConfig?.path ?? ''}`);
    }

    const routeConfigKey = routeConfigParts.join('>');

    const queryParamKeys = Object.keys(route.queryParams).sort();
    const queryParts: string[] = [];

    for (let i = 0; i < queryParamKeys.length; i++) {
      const paramKey = queryParamKeys[i];
      const value = route.queryParams[paramKey];
      const serialized = JSON.stringify(value) ?? String(value);

      queryParts.push(`${encodeURIComponent(paramKey)}=${encodeURIComponent(serialized)}`);
    }

    const queryParams = queryParts.join('&');
    const fragment = route.fragment ?? '';

    return `${routeConfigKey}|${path}?${queryParams}#${fragment}`;
  }

  /**
   * Determina a qué pestaña abierta pertenece una ruta.
   *
   * Si existen varias coincidencias usa la más específica.
   */
  private findTabId(path: string): string | null {
    const tabs = this.tabsService.tabs();

    let selectedId: string | null = null;

    for (let i = 0; i < tabs.length; i++) {
      const tab = tabs[i];

      const matches = path === tab.id || path.startsWith(`${tab.id}/`);

      if (!matches) {
        continue;
      }

      if (selectedId === null || tab.id.length > selectedId.length) {
        selectedId = tab.id;
      }
    }

    return selectedId;
  }

  private isTabOpen(tabId: string): boolean {
    const tabs = this.tabsService.tabs();

    for (let i = 0; i < tabs.length; i++) {
      if (tabs[i].id === tabId) {
        return true;
      }
    }

    return false;
  }

  /**
   * DetachedRouteHandle es opaco en la API pública de Angular 19.
   * Internamente contiene el ComponentRef que debemos destruir cuando una
   * pestaña se cierra definitivamente.
   */
  private destroyHandle(handle: DetachedRouteHandle): void {
    const internalHandle = handle as AngularDetachedRouteHandle;

    try {
      internalHandle.componentRef?.destroy();
    } catch (error) {
      console.warn('[TabRouteReuseStrategy] No se pudo destruir el componente almacenado.', error);
    }
  }
}
